import { useState } from "react";
import { Loader2, Lock } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import { IDS } from "@/lib/testIds";
import { loadOperatorSubmissions } from "@/lib/api";

const STATUS_TONE = {
  EMAIL_PROVIDER_UNCONFIGURED: "bg-amber-soft text-amber-deep border-amber-line",
  EMAIL_NOTIFICATION_PENDING: "bg-stone-soft text-muted border-muted-line",
  EMAIL_NOTIFICATION_SENT: "bg-teal-soft text-teal-deep border-teal/30",
  EMAIL_NOTIFICATION_FAILED: "bg-red-50 text-red-700 border-red-200",
};

export default function Operator() {
  const [token, setToken] = useState("");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setError("");
    setLoading(true);
    try {
      const data = await loadOperatorSubmissions(token.trim());
      setRows(data);
    } catch (err) {
      setError(
        err?.response?.status === 401
          ? "Operator authorisation required. Check the operator token."
          : "Could not load submissions."
      );
      setRows(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid={IDS.operatorRoot} className="min-h-screen bg-stone-base paper-grain">
      <header className="mx-auto max-w-5xl px-6 pt-10">
        <BrandMark subtitle="Operator view" />
      </header>
      <main className="mx-auto max-w-5xl px-6 py-10">
        <div className="flex items-center gap-2 text-teal-deep">
          <Lock className="h-4 w-4" />
          <h1 className="font-serif text-[26px] text-ink">Protected operator record</h1>
        </div>
        <p className="mt-2 max-w-xl text-[14px] leading-relaxed text-muted">
          Stored invited-discovery submissions. Access requires the operator
          token held only as a backend secret.
        </p>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label htmlFor={IDS.operatorTokenInput} className="text-[13px] font-semibold text-ink">
              Operator token
            </label>
            <input
              id={IDS.operatorTokenInput}
              data-testid={IDS.operatorTokenInput}
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              className="mt-1.5 w-full rounded-lg border border-muted-line bg-white px-3.5 py-2.5 font-mono text-[14px] text-ink focus:border-teal"
              placeholder="operator token"
            />
          </div>
          <button
            data-testid={IDS.operatorLoadButton}
            onClick={load}
            disabled={loading || !token.trim()}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-ink px-6 py-2.5 text-[14px] font-medium text-stone-soft transition-colors hover:bg-ink-deep disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Load submissions"}
          </button>
        </div>

        {error && <p className="mt-4 text-[13.5px] font-medium text-red-600">{error}</p>}

        {rows && (
          <div className="mt-8 overflow-x-auto rounded-xl border border-muted-line bg-white">
            <table data-testid={IDS.operatorTable} className="w-full text-left text-[13px]">
              <thead className="bg-stone-soft text-[11px] uppercase tracking-wide text-muted">
                <tr>
                  <th className="px-4 py-3">Reference</th>
                  <th className="px-4 py-3">Submitted (UK)</th>
                  <th className="px-4 py-3">LinkedIn</th>
                  <th className="px-4 py-3">Website</th>
                  <th className="px-4 py-3">Report email</th>
                  <th className="px-4 py-3">Notification</th>
                  <th className="px-4 py-3">Retention</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-muted-line/60">
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-6 text-center text-muted">
                      No submissions yet.
                    </td>
                  </tr>
                )}
                {rows.map((r) => (
                  <tr key={r.reference} className="align-top text-ink-soft">
                    <td className="px-4 py-3 font-mono text-[12px] text-ink">{r.reference}</td>
                    <td className="px-4 py-3">{r.submitted_readable_uk}</td>
                    <td className="max-w-[180px] truncate px-4 py-3">
                      <a href={r.linkedin_url} target="_blank" rel="noreferrer" className="text-teal-deep underline-offset-2 hover:underline">
                        {r.linkedin_url}
                      </a>
                    </td>
                    <td className="max-w-[180px] truncate px-4 py-3">
                      <a href={r.website_url} target="_blank" rel="noreferrer" className="text-teal-deep underline-offset-2 hover:underline">
                        {r.website_url}
                      </a>
                    </td>
                    <td className="px-4 py-3 font-mono text-[12px]">{r.report_email}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block rounded-full border px-2.5 py-1 text-[11px] font-medium ${
                          STATUS_TONE[r.email_notification_status] || "bg-stone-soft text-muted border-muted-line"
                        }`}
                      >
                        {r.email_notification_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[12px]">{r.retention_review_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
